import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { setFullViewportWidthStyles, theme } from "shared/lib/styles";

import { Container as HeaderFeaturesSearchContainer } from "scmp-app/components/header/features/search/styles";

import { StyledPostMagazineLogoFlag } from "./features/post-magazine-logo-link/styles";
import { StyledPostMagazineLogoText } from "./features/post-magazine-logo-text-link/styles";

type ContainerProps = {
  $isMagazineStyle: boolean;
};

export const Container = styled.div<ContainerProps>`
  ${setFullViewportWidthStyles()}
  display: flex;
  flex-flow: column nowrap;
  border-block-end: 1px solid #000000;

  & {
    padding-inline: 12px;

    ${theme.breakpoints.up("tablet")} {
      padding-inline: 24px;
    }

    ${theme.breakpoints.up("mediumDesktop")} {
      display: flex;
      justify-content: center;
    }
  }

  ${props =>
    props.$isMagazineStyle &&
    css`
      padding-inline: 16px 12px;

      ${theme.breakpoints.up("tablet")} {
        padding-inline: 32px;
      }

      ${HeaderContainer} {
        ${theme.breakpoints.up("largeDesktop")} {
          inline-size: 1360px;
        }
      }

      ${theme.breakpoints.only("mobile")} {
        border-block-end: none;
      }
    `}
`;

export const HeaderContainer = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: flex-start;
  align-items: center;

  inline-size: 100%;
  block-size: 45px;
  margin-inline: auto;
  padding-block: 12px;
  box-sizing: border-box;

  background-color: transparent;

  ${theme.breakpoints.up("tablet")} {
    block-size: 48px;
  }

  ${theme.breakpoints.up("desktop")} {
    block-size: 54px;
    max-inline-size: 1392px;
  }
`;

export const LeftFeatureContainer = styled.div<{
  $isPostMagazine?: boolean;
  $isPostMagazineArticle?: boolean;
  $shouldSwap?: boolean;
}>`
  display: flex;
  column-gap: 6px;
  align-items: center;

  ${theme.breakpoints.up("tablet")} {
    column-gap: 16px;
  }

  ${theme.breakpoints.up("desktop")} {
    column-gap: 20px;
  }

  ${props =>
    props.$isPostMagazine &&
    css`
      column-gap: 8px;

      ${theme.breakpoints.up("tablet")} {
        column-gap: 16px;
      }

      ${theme.breakpoints.up("desktop")} {
        column-gap: 24px;
      }
    `}

  ${props =>
    props.$isPostMagazineArticle &&
    css`
      column-gap: ${props.$shouldSwap ? "12px" : "8px"};

      ${theme.breakpoints.up("tablet")} {
        column-gap: 16px;
      }

      ${theme.breakpoints.up("desktop")} {
        column-gap: 24px;
      }
    `}
`;

export const CenterFeatureContainer = styled.div`
  display: none;
  ${theme.breakpoints.up("desktop")} {
    position: relative;

    display: flex;
    flex: 1 1 auto;
    flex-direction: row;
    justify-content: center;

    min-inline-size: 0;
    padding-inline: 20px;
  }
`;

export const AbsoluteCenterFeatureContainer = styled.div`
  position: absolute;
  inset-block-start: 50%;
  inset-inline-start: 50%;

  transform: translate(-50%, -50%);
`;

export const RightFeatureContainer = styled.div<{ $shouldSwap?: boolean }>`
  display: flex;
  column-gap: ${({ $shouldSwap }) => ($shouldSwap ? "8px" : "16px")};
  justify-content: flex-end;
  align-items: center;

  margin-inline-start: auto;
`;

export const HomeMiniHeaderCenterWrapper = styled.div`
  display: flex;
  justify-content: center;

  inline-size: 100%;
`;

type ArticleHamburgerMenuLogoWrapperProps = {
  $shouldSwap?: boolean;
};

export const ArticleHamburgerMenuLogoWrapper = styled.div<ArticleHamburgerMenuLogoWrapperProps>`
  display: flex;
  column-gap: 14px;

  > *:empty {
    display: none !important;
  }

  ${theme.breakpoints.up("tablet")} {
    display: ${({ $shouldSwap }) => ($shouldSwap ? "flex" : "none")};
  }
`;

export const ArticleSearchSectionWrapper = styled.div`
  display: flex;
  align-items: center;

  ${theme.breakpoints.up("desktop")} {
    column-gap: 32px;
  }
`;

export const ArticleSectionAllTabWrapper = styled.div`
  display: flex;
  column-gap: 12px;
`;

export const ArticleIconsWrapper = styled.div`
  display: flex;
  column-gap: 12px;
`;

export const StyleHeaderLogoContainer = styled.div`
  margin-inline: auto;
  padding-block: 48px 10px;

  ${theme.breakpoints.up("desktop")} {
    padding-block-start: 64px;
  }

  ${theme.breakpoints.only("mobile")} {
    display: none;
  }
`;

export const StyleLeftWrapper = styled.div`
  display: flex;
  column-gap: 12px;
`;

export const StyleLogoWrapper = styled.div`
  position: absolute;
  inset-block-start: 50%;
  inset-inline-start: 50%;

  display: flex;
  gap: 12px;

  transform: translate(-50%, -50%);
`;

export const DefaultLeftWrapper = styled.div`
  display: flex;
  gap: 16px;

  ${theme.breakpoints.up("desktop")} {
    gap: 32px;
  }
`;

export const PostMagazineArticleCenterFeatureContainer = styled.div`
  display: none;

  ${theme.breakpoints.up("desktop")} {
    position: relative;

    display: flex;
    flex: 1 1 auto;
    flex-direction: row;
    justify-content: center;
    align-items: center;

    min-inline-size: 0;
  }
`;

export const PostMagazineArticleCenterSectionWrapper = styled.div`
  text-transform: uppercase;
`;

export const PostMagazineLeftWrapper = styled.div`
  display: flex;
  align-items: center;

  ${HeaderFeaturesSearchContainer} {
    ${theme.breakpoints.up("desktop")} {
      margin-inline-start: 24px;
    }
  }
`;

export const PostMagazineLogoWrapper = styled.div`
  display: flex;
  column-gap: 12px;
  align-items: center;

  ${theme.breakpoints.only("mobile")} {
    column-gap: 9.72px;
    ${StyledPostMagazineLogoFlag} {
      inline-size: 20.6px;
    }

    ${StyledPostMagazineLogoText} {
      inline-size: 68.8px;
      margin-block-start: 3.53px;
    }
  }
`;
